import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Tag } from '../types/tag.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_TAGS_API_PATHS } from '../constants/tags-api-paths.constants';

interface FindTagsResponse {
  totalCount: number;
  tags: Tag[];
}

export interface UseFindTagsParams {
  page: number;
  limit: number;
  name?: string;
}

type UseFindTagsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  tags: Tag[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseFindTagsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  tags: [],
  totalCount: 0,
};

export function useFindTags({ page, limit, name }: UseFindTagsParams): UseFindTagsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<FindTagsResponse>(ADMIN_TAGS_API_PATHS.GET_TAGS, {
        params: {
          page,
          limit,
          sortBy: 'name',
          sortDirection: 'asc',
          filters: name ? JSON.stringify({ name }) : undefined,
        },
      });

      setResult({ isLoading: false, isRefetching: false, tags: response.tags, totalCount: response.totalCount });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des tags'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, tags: [], totalCount: 0 });
    }
  }, [page, limit, name, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
